import React, { Component } from 'react';
import { Row, Col } from 'antd';
import { observer, inject } from 'mobx-react';
import { withTranslation } from 'react-i18next';
import './HomeEventAndFacility.css';

@inject('CmsEventHotelStore', 'CmsFacilityHotelStore', 'templateStore')
@observer
class HomeEventAndFacility extends Component {
  constructor(props) {
    super(props);
    this.state = {
      currentPage: 0,
      cardsPerPage: 4,
      touchStartX: 0,
      touchStartY: 0,
      touchEndX: 0,
      isTransitioning: false,
      containerWidth: 0,
      swiping: false,
      swipeOffset: 0,
    };
    this.carouselRef = React.createRef();
    this.trackRef = React.createRef();
    this.resizeTimer = null;
  }

  componentDidMount() {
    this.updateCardsPerPage();
    window.addEventListener('resize', this.handleResize);

    // 参考 HotelEvent 和 HotelFacility 的接口调用方式，获取线上数据
    const fetchEvents = this.props.CmsEventHotelStore.getAllByLanguage();
    const fetchFacilities = this.props.CmsFacilityHotelStore.getAllByLanguage();

    Promise.all([fetchEvents, fetchFacilities]).then(() => {
      const aggregatedData = this.getAggregatedData();
      console.log('Aggregated Event & Facility Data:', aggregatedData);
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
  }

  // ==================== Responsive ====================

  handleResize = () => {
    if (this.resizeTimer) clearTimeout(this.resizeTimer);
    this.resizeTimer = setTimeout(() => {
      this.updateCardsPerPage();
    }, 150);
  };

  updateCardsPerPage = () => {
    const width = window.innerWidth;
    const containerWidth = this.carouselRef.current
      ? this.carouselRef.current.offsetWidth
      : 0;

    let cardsPerPage;
    if (width < 740) {
      cardsPerPage = 3;
    } else if (containerWidth > 1000) {
      // Card aspect height = width * 1.7074, limit height to 360px → max card width ≈ 211px
      const maxCardHeight = 360;
      const aspectRatio = 1.7074;
      const maxCardWidth = Math.floor(maxCardHeight / aspectRatio);
      const gapPx = 16;
      // Calculate minimum cards needed to keep each card ≤ maxCardWidth
      cardsPerPage = Math.ceil((containerWidth + gapPx) / (maxCardWidth + gapPx));
      // Ensure at least 4 cards
      cardsPerPage = Math.max(cardsPerPage, 4);
    } else {
      cardsPerPage = 4;
    }

    this.setState((prevState) => ({
      cardsPerPage,
      containerWidth,
      currentPage: Math.min(
        prevState.currentPage,
        Math.max(0, Math.ceil(this.getData().length / cardsPerPage) - 1)
      ),
    }));
  };

  // ==================== Data ====================

  /**
   * 将 store 中的单条数据项转换为卡片渲染所需的统一格式
   * 参考 HotelEvent / HotelFacility 的数据结构进行字段映射
   */
  transformToCard = (item, source) => {
    // 解析 bannerUrl JSON 字符串，取第一张图片 URL
    let imageUrl = '';
    try {
      if (item.bannerUrl) {
        const images = typeof item.bannerUrl === 'string'
          ? JSON.parse(item.bannerUrl)
          : item.bannerUrl;
        if (Array.isArray(images) && images.length > 0) {
          imageUrl = images[0].url || '';
        }
      }
    } catch (e) {
      console.warn('Failed to parse bannerUrl:', e);
    }

    // 标题：优先取扁平化的 title，其次从多语言数组中取
    let title = item.title || '';
    if (!title && item.eventHotelLanguages && item.eventHotelLanguages.length > 0) {
      title = item.eventHotelLanguages[0].title || '';
    }
    if (!title && item.facilityHotelLanguages && item.facilityHotelLanguages.length > 0) {
      title = item.facilityHotelLanguages[0].title || '';
    }

    // 描述：优先 briefDescription，其次 description（去除 HTML 标签用于卡片展示）
    let description = item.briefDescription || item.description || '';
    if (!description && item.eventHotelLanguages && item.eventHotelLanguages.length > 0) {
      description = item.eventHotelLanguages[0].briefDescription
        || item.eventHotelLanguages[0].description || '';
    }
    if (!description && item.facilityHotelLanguages && item.facilityHotelLanguages.length > 0) {
      description = item.facilityHotelLanguages[0].briefDescription
        || item.facilityHotelLanguages[0].description || '';
    }
    description = description.replace(/<[^>]*>/g, '');

    return {
      id: `${source}-${item.id}`,
      imageUrl,
      title,
      description,
      source,
      originalId: item.id,
      onClick: () => {
        if (source === 'event') {
          this.props.history.push(`/event/${item.id}`);
        } else if (source === 'facility') {
          this.props.history.push(`/facility/${item.id}`);
        }
      },
    };
  };

  /**
   * 聚合 CmsEventHotelStore 和 CmsFacilityHotelStore 的数据为统一数组
   */
  getAggregatedData = () => {
    const { CmsEventHotelStore, CmsFacilityHotelStore } = this.props;
    const events = CmsEventHotelStore.eventHotels || [];
    const facilities = CmsFacilityHotelStore.facilityHotels || [];

    const eventCards = events.map(item => this.transformToCard(item, 'event'));
    const facilityCards = facilities.map(item => this.transformToCard(item, 'facility'));

    return [...eventCards, ...facilityCards];
  };

  getData = () => {
    const { data } = this.props;
    if (data && data.length) return data;

    const aggregated = this.getAggregatedData();
    return aggregated;
  };

  getTotalPages = () => {
    const { cardsPerPage } = this.state;
    const data = this.getData();
    if (!data.length) return 0;
    return Math.ceil(data.length / cardsPerPage);
  };

  // ==================== Touch Handlers ====================

  handleTouchStart = (e) => {
    this.setState({
      touchStartX: e.touches[0].clientX,
      touchStartY: e.touches[0].clientY,
      swiping: false,
      swipeOffset: 0,
      isTransitioning: true,
    });
  };

  handleTouchMove = (e) => {
    const { touchStartX, touchStartY } = this.state;
    const currentX = e.touches[0].clientX;
    const currentY = e.touches[0].clientY;
    const deltaX = currentX - touchStartX;
    const deltaY = currentY - touchStartY;

    // Only activate horizontal swipe if horizontal movement > vertical
    if (!this.state.swiping && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
      this.setState({ swiping: true });
    }

    if (this.state.swiping || Math.abs(deltaX) > Math.abs(deltaY)) {
      this.setState({ swipeOffset: deltaX });
    }
  };

  handleTouchEnd = () => {
    const { swipeOffset } = this.state;
    const threshold = 80;

    this.setState({ swiping: false, swipeOffset: 0 });

    if (Math.abs(swipeOffset) > threshold) {
      if (swipeOffset < 0) {
        // Swipe left → next page
        this.goToNext();
      } else {
        // Swipe right → previous page
        this.goToPrev();
      }
    } else {
      this.setState({ isTransitioning: false });
    }
  };

  // ==================== Navigation ====================

  goToPage = (pageIndex) => {
    const { currentPage, isTransitioning } = this.state;
    const totalPages = this.getTotalPages();
    if (isTransitioning || pageIndex === currentPage) return;
    if (pageIndex < 0) pageIndex = 0;
    if (pageIndex >= totalPages) pageIndex = totalPages - 1;
    this.setState({
      isTransitioning: true,
      currentPage: pageIndex,
    });
    setTimeout(() => this.setState({ isTransitioning: false }), 450);
  };

  goToNext = () => {
    const { currentPage } = this.state;
    const totalPages = this.getTotalPages();
    if (currentPage < totalPages - 1) {
      this.setState({
        isTransitioning: true,
        currentPage: currentPage + 1,
      });
      setTimeout(() => this.setState({ isTransitioning: false }), 450);
    }
  };

  goToPrev = () => {
    const { currentPage } = this.state;
    if (currentPage > 0) {
      this.setState({
        isTransitioning: true,
        currentPage: currentPage - 1,
      });
      setTimeout(() => this.setState({ isTransitioning: false }), 450);
    }
  };

  // ==================== Render Helpers ====================

  renderCard = (item, index) => {
    const { activeTemplate } = this.props.templateStore;

    return (
      <div
        className="stm-efc-card"
        key={item.id || index}
        style={{
          backgroundImage: item.imageUrl
            ? `url(${item.imageUrl})`
            : 'none',
          backgroundColor: item.imageUrl ? 'transparent' : '#e8e8e8',
        }}
        onClick={() => item.onClick && item.onClick(item)}
      >
        {/* Placeholder when no image */}
        {!item.imageUrl && (
          <div className="stm-efc-card-image-placeholder">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
          </div>
        )}

        {/* Overlay: title + description */}
        <div className="stm-efc-card-overlay">
          <div
            className="stm-efc-card-overlay-title"
            style={{
              fontFamily: activeTemplate?.templateSetting.label.fontFamily,
            }}
          >
            {item.title || ''}
          </div>
          <div className="stm-efc-card-overlay-desc"
          style={{
              fontFamily: activeTemplate?.templateSetting.text.fontFamily,
            }}>
            {item.description || ''}
          </div>
        </div>
      </div>
    );
  };

  // ==================== Render ====================

  render() {
    const { t } = this.props;
    const { activeTemplate } = this.props.templateStore;
    const {
      currentPage,
      cardsPerPage,
      isTransitioning,
      swiping,
      swipeOffset,
    } = this.state;
    const data = this.getData();
    const totalPages = this.getTotalPages();
    const isLastPage = currentPage >= totalPages - 1;
    const isFirstPage = currentPage <= 0;
    const singlePage = totalPages <= 1;

    // 末页卡片不足时，最后一页的起始卡片索引
    const lastPageStartIndex = (totalPages - 1) * cardsPerPage;
    const itemsOnLastPage = data.length - lastPageStartIndex;
    const lastPageIncomplete = isLastPage && itemsOnLastPage < cardsPerPage;

    // Calculate translateX in pixels for precise clipping
    const containerWidth = this.carouselRef.current?.offsetWidth || 0;
    const gapPx = cardsPerPage >= 4 ? 16 : 8;
    const totalGapPx = (cardsPerPage - 1) * gapPx;
    const cardWidthPx = containerWidth > totalGapPx
      ? Math.floor((containerWidth - totalGapPx) / cardsPerPage)
      : 0;
    const slotWidth = cardWidthPx + gapPx; // 卡片 + 间隙 = 一个槽位宽

    // 末页不足一页时，强制展示最后 cardsPerPage 张卡片（从 data.length - cardsPerPage 开始）
    // 仅当总页数 > 1 时才需要对末页做对齐处理，单页时始终从 0 开始左对齐
    const startIndex = (lastPageIncomplete && !singlePage)
      ? data.length - cardsPerPage
      : currentPage * cardsPerPage;
    const baseTranslatePx = -(startIndex * slotWidth);
    const swipeTranslatePx = swiping ? swipeOffset : 0;
    const translateX = baseTranslatePx + swipeTranslatePx;

    if (!data.length) {
      return (
        <div className="stm-efc-empty">
          {t ? t('noData') : 'No data available'}
        </div>
      );
    }

    return (
      <div className="stm-efc-container stm-padding-10-20">
        <Row className="stm-margin-bottom-10">
          <Col
            span={18}
            className='stm-font-weight-bold'
            style={{
              fontFamily: activeTemplate?.templateSetting.label.fontFamily,
              fontSize: `18px`
            }}
          >
            Explore Our Hotel
          </Col>
          <Col
            span={6}
            className='stm-text-align-right stm-font-weight-bold stm-efc-see-all'
            onClick={() => this.props.history.push('/event-facility-all')}
            style={{
              fontFamily: activeTemplate?.templateSetting.label.fontFamily,
              fontSize: `16px`
            }}
          >
            See All
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              style={{ verticalAlign: 'middle', marginLeft: 2, position: 'relative', top: -1 }}>
              <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Col>
        </Row>
        {/* Carousel Wrapper */}
        <div className="stm-efc-carousel">
          {/* Viewport: overflow + touch events */}
          <div
            className="stm-efc-viewport"
            ref={this.carouselRef}
            onTouchStart={this.handleTouchStart}
            onTouchMove={this.handleTouchMove}
            onTouchEnd={this.handleTouchEnd}
          >
            {/* Track */}
            <div
              ref={this.trackRef}
              className="stm-efc-track"
              style={{
                transform: `translateX(${translateX}px)`,
                transition: isTransitioning && !swiping
                  ? 'transform 0.45s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                  : 'none',
              }}
            >
              {data.map((item, index) => (
                <div
                  key={item.id || index}
                  className="stm-efc-card-wrapper"
                  style={{ width: `${cardWidthPx}px` }}
                >
                  {this.renderCard(item, index)}
                </div>
              ))}
            </div>
          </div>

          {/* Left Arrow Button */}
          {!singlePage && !isFirstPage && (
            <button
              className="stm-efc-arrow stm-efc-arrow-left"
              onClick={this.goToPrev}
              aria-label="Previous page"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M15 6L9 12L15 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}

          {/* Right Arrow Button */}
          {!singlePage && !isLastPage && (
            <button
              className="stm-efc-arrow stm-efc-arrow-right"
              onClick={this.goToNext}
              aria-label="Next page"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <path
                  d="M9 6L15 12L9 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Page Indicator */}
        {!singlePage && (
          <div className="stm-efc-indicator">
            {Array.from({ length: totalPages }, (_, i) => (
              <span
                key={i}
                className={`stm-efc-dot ${i === currentPage ? 'stm-efc-dot-active' : ''}`}
                onClick={() => this.goToPage(i)}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
}

export default withTranslation()(HomeEventAndFacility);
